import { useState, useEffect, useRef, useCallback } from "react";
import { FeatureExplorerMedia } from "@/components/marketing/feature-explorer-media";
import {
  DollarSign,
  Home,
  TrendingUp,
  CheckCircle,
  Edit3,
  Users,
  Shield,
  ClipboardList,
  ImageIcon,
  Wrench,
  MessageSquare,
  BarChart3,
  Lock,
  Thermometer,
  Bot,
  RefreshCw,
  Map,
  FileText,
  Package,
  Building2,
  ChevronRight,
  Activity,
  Cpu,
  Calendar,
  Globe,
  ExternalLink,
  Car,
  Clock,
} from "lucide-react";


export const featureGroups = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: BarChart3,
    color: "from-rose-500 to-pink-600",
    bgColor: "bg-rose-500/10",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "The single command center for your entire portfolio. Every detail at a glance, everything you need, nothing you don't — customizable based on what matters most to you or your team at a moment's notice.",
    features: [
      { icon: Activity, name: "Occupancy Metrics", detail: "Live portfolio-wide and per-property occupancy rates" },
      { icon: Wrench, name: "Maintenance Alerts", detail: "Outstanding tasks and overdue work orders surfaced instantly" },
      { icon: Thermometer, name: "Thermostat Health", detail: "Device status, temperature anomalies, and offline alerts" },
      { icon: BarChart3, name: "Financial Snapshots", detail: "Revenue, expenses, and NOI at a portfolio glance" },
      { icon: Lock, name: "Lock Health", detail: "Smart lock connectivity and battery status across properties" },
      { icon: TrendingUp, name: "Performance Trends", detail: "Occupancy, revenue, and expense trends over time" },
    ],
    mockUI: "dashboard",
  },
  {
    id: "property-management",
    label: "Property Management",
    icon: Building2,
    color: "from-blue-500 to-cyan-600",
    bgColor: "bg-blue-500/10",
    textColor: "text-blue-400",
    borderColor: "border-blue-500/30",
    description: "Full property and room tracking with Zillow import, interactive Google Maps, detailed property profiles, and multi-unit support.",
    features: [
      { icon: Home, name: "Zillow Import", detail: "One-click property import from Zillow listings" },
      { icon: Map, name: "Google Maps Integration", detail: "Interactive map view with property markers" },
      { icon: Building2, name: "Property Profiles", detail: "Detailed property info, photos, and history" },
      { icon: Home, name: "Room Management", detail: "Individual room records, status, and occupancy" },
      { icon: Users, name: "Multi-Unit Support", detail: "From a single home to 1000s of units — single-family, duplex, quadplex and beyond, all managed from one dashboard" },
      { icon: Activity, name: "Occupancy Metrics", detail: "Real-time occupancy rates and trend tracking" },
    ],
    mockUI: "property",
  },
  {
    id: "underwriting",
    label: "Underwriting Analysis",
    icon: ClipboardList,
    color: "from-purple-500 to-indigo-600",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-400",
    borderColor: "border-purple-500/30",
    description: "Comprehensive 11-section financial calculator with branded PDF exports, configurable defaults, and map view with property markers.",
    features: [
      { icon: BarChart3, name: "11-Section Calculator", detail: "Income, expenses, financing, cap rate & more" },
      { icon: FileText, name: "Branded PDF Export", detail: "Polished reports with your company branding" },
      { icon: Map, name: "Map View", detail: "Property markers with underwriting data overlays" },
      { icon: DollarSign, name: "Configurable Defaults", detail: "Save preferred assumptions and templates" },
      { icon: TrendingUp, name: "One-Click LTR Import", detail: "Import long-term rent comps instantly for faster underwriting" },
      { icon: Building2, name: "Lender Directory", detail: "Integrated lender contacts and loan terms" },
    ],
    mockUI: "underwriting",
  },
  {
    id: "direct-booking",
    label: "Direct Booking Website",
    icon: Globe,
    color: "from-brand-accent to-[#ff6b00]",
    bgColor: "bg-brand-accent/10",
    textColor: "text-brand-accent",
    borderColor: "border-brand-accent/30",
    description: "Your own verified, branded booking website that builds trust and converts listings. Capture inquiries, auto-sync room availability, and eliminate 10-day booking fees — saving $250 to $300 per booking.",
    features: [
      { icon: Shield, name: "Trust & Credibility", detail: "Verified, authentic branded site that builds trust and helps convert listings" },
      { icon: Users, name: "Lead Capture", detail: "Capture member inquiry contact information with a built-in lead-in box" },
      { icon: DollarSign, name: "No 10-Day Booking Fees", detail: "Skip PadSplit's 10-day booking fees — save $250–$300 per booking" },
      { icon: RefreshCw, name: "Auto Room Availability", detail: "Automatically maintains and syncs live room availability" },
      { icon: Edit3, name: "Customizable Branding", detail: "Your logo, colors, and property descriptions — fully branded" },
      { icon: Home, name: "Room Showcase", detail: "Live room listings with photos, pricing, and availability status" },
    ],
    mockUI: "directBooking",
  },
  {
    id: "listing-management",
    label: "Listing Management",
    icon: ExternalLink,
    color: "from-fuchsia-500 to-purple-600",
    bgColor: "bg-fuchsia-500/10",
    textColor: "text-fuchsia-400",
    borderColor: "border-fuchsia-500/30",
    description: "Track and manage your room listings across every platform from one place. Zillow, Facebook Marketplace, Roomies, HotPads, SpareRoom, Furnished Finders — or any other channel you use. *Under development for some marketplaces.",
    features: [
      { icon: ExternalLink, name: "Multi-Platform Tracking", detail: "Manage listings on Zillow, Facebook, Roomies, HotPads, SpareRoom & more" },
      { icon: DollarSign, name: "Track Pricing", detail: "Track pricing across platforms — keep rates consistent or customize per channel" },
      { icon: BarChart3, name: "Bird's-Eye Channel View", detail: "See at a glance which properties are listed on which channels — so you never miss an opportunity" },
      { icon: Calendar, name: "Listing Reminders", detail: "Never forget to refresh a listing — automated reminders keep every platform current" },
    ],
    mockUI: "listingManagement",
  },
  {
    id: "dynamic-pricing",
    label: "Dynamic Pricing",
    icon: TrendingUp,
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-500/10",
    textColor: "text-green-400",
    borderColor: "border-green-500/30",
    description: "Automatically reprice your rooms based on your investment goals. Optimize at a per-room level for fill rate or for profit. Set your rules, apply per room, then sit back and let PadPulse drive your revenue management.",
    features: [
      { icon: DollarSign, name: "Room-Level Repricing", detail: "Unique pricing rules per room type and location" },
      { icon: RefreshCw, name: "Auto-Calculated Rates", detail: "Weekly and monthly rates derived automatically" },
      { icon: Activity, name: "Occupancy-Based Rules", detail: "Auto-adjust prices based on vacancy thresholds" },
      { icon: Calendar, name: "End-of-Month Pricing", detail: "Automatically adjust rates as month-end approaches to drive fill and capture last-minute demand" },
    ],
    mockUI: "pricing",
  },
  {
    id: "operations",
    label: "Operations",
    icon: Wrench,
    color: "from-orange-500 to-red-500",
    bgColor: "bg-orange-500/10",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/30",
    description: "Full operations suite: Maintenance Kanban with bi-directional PadSplit sync, vendor CRM, vendor portal with SMS, service forms with photo uploads, and supply management.",
    features: [
      { icon: ClipboardList, name: "Maintenance Kanban", detail: "Visual task boards with bi-directional PadSplit sync — maintenance tickets created in PadSplit appear here, and vice versa" },
      { icon: Users, name: "Vendor CRM", detail: "Keep ratings, notes, and every vendor relationship in one place" },
      { icon: MessageSquare, name: "Vendor SMS Portal", detail: "Tired of juggling sporadic emails and texts? Vendors get their own portal with SMS notifications — no app download required" },
      { icon: FileText, name: "Service Forms", detail: "No more juggling or creating third-party forms — photo uploads, geolocation, and digital signatures built right in" },
      { icon: Package, name: "Supply Management", detail: "Set your order items and timelines. Stay on track with zero headaches and get alerted if things are used up faster than normal" },
      { icon: Activity, name: "Vendor Payments", detail: "Approval queue for completed tasks with ratings and bulk approval" },
    ],
    mockUI: "operations",
  },
  {
    id: "members",
    label: "Member Management",
    icon: Users,
    color: "from-teal-500 to-cyan-600",
    bgColor: "bg-teal-500/10",
    textColor: "text-teal-400",
    borderColor: "border-teal-500/30",
    description: "Complete member lifecycle management: PadSplit member sync, profiles, vehicle & parking registry, and a smart messaging inbox with PadSplit Messenger integration, rules-based automation, and full threading.",
    features: [
      { icon: Users, name: "Member Profiles", detail: "PadSplit members auto-synced — complete history and notes in one place" },
      { icon: Car, name: "Vehicle & Parking Registry", detail: "Log vehicles and quickly find any owner in seconds — no need to comb through PadSplit or multiple spreadsheets" },
      { icon: MessageSquare, name: "Messaging Inbox", detail: "A centralized communication hub for all your PadSplit messages" },
      { icon: Bot, name: "Rule-Based Automation", detail: "Auto-respond to common inquiries and triggers" },
      { icon: MessageSquare, name: "Threaded Conversations", detail: "Talk internally with any team member, turn any message into a task or supply order" },
      { icon: Clock, name: "Waiting Reply Tracker", detail: "Mark member-sent messages as waiting reply and never lose track of follow-ups" },
    ],
    mockUI: "members",
  },
  {
    id: "financials",
    label: "Financials",
    icon: DollarSign,
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-500/10",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/30",
    description: "Complete financial suite: Profit & Loss, expense tracking, bank-imported transactions, and a lender directory.",
    features: [
      { icon: BarChart3, name: "Profit & Loss", detail: "Real-time P&L by property and portfolio" },
      { icon: DollarSign, name: "Expense Tracking", detail: "Manual and bank-imported expense categorization" },
      { icon: Building2, name: "Lender Directory", detail: "Your lender contacts, terms, and loan details" },
    ],
    mockUI: "financials",
  },
  {
    id: "smart-devices",
    label: "Smart Devices",
    icon: Cpu,
    color: "from-cyan-500 to-blue-600",
    bgColor: "bg-cyan-500/10",
    textColor: "text-cyan-400",
    borderColor: "border-cyan-500/30",
    description: "Monitor and control smart locks* and thermostats across all your properties from one unified dashboard.",
    features: [
      { icon: Lock, name: "Smart Lock Control", detail: "Remote lock/unlock with per-member access codes" },
      { icon: Thermometer, name: "Thermostat Monitoring", detail: "Temperature monitoring and HVAC health alerts" },
      { icon: Shield, name: "Access Code Management", detail: "Time-limited codes for vendors and guests" },
      { icon: Activity, name: "Device Health Dashboard", detail: "Automatically notify members to change batteries when locks are running low" },
      { icon: Lock, name: "Vendor Lock Access", detail: "Grant vendors time-limited access codes to smart locks for scheduled work" },
    ],
    mockUI: "devices",
  },
  {
    id: "ai-tools",
    label: "AI Tools",
    icon: Bot,
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-500/10",
    textColor: "text-pink-400",
    borderColor: "border-pink-500/30",
    description: "AI-powered image generator for rooms, bathrooms, exteriors, and common spaces — produce professional listing photos instantly.",
    features: [
      { icon: ImageIcon, name: "Room Photo Generator", detail: "AI-generated professional interior photos" },
      { icon: ImageIcon, name: "Bathroom Renders", detail: "Stylized bathroom visuals for listings" },
      { icon: ImageIcon, name: "Exterior Shots", detail: "Curb appeal images for any property type" },
      { icon: ImageIcon, name: "Common Space Visuals", detail: "Living room, kitchen, and shared space renders" },
      { icon: Edit3, name: "Custom Prompts", detail: "Fine-tune style, lighting, and staging details" },
      { icon: Bot, name: "AI-Powered", detail: "State-of-the-art AI image generation" },
    ],
    mockUI: "ai",
  },
  {
    id: "padsplit",
    label: "PadSplit Sync",
    icon: RefreshCw,
    color: "from-indigo-500 to-blue-600",
    bgColor: "bg-indigo-500/10",
    textColor: "text-indigo-400",
    borderColor: "border-indigo-500/30",
    description: "Connects to your PadSplit account: automated sync engine, knowledge base, bulk data import, and member sync.",
    features: [
      { icon: RefreshCw, name: "Automated Sync Engine", detail: "Real-time data sync with your PadSplit account" },
      { icon: FileText, name: "Knowledge Base", detail: "PadSplit policies and procedures at your fingertips" },
      { icon: Activity, name: "Data Import", detail: "Bulk import members, rooms, and history" },
      { icon: MessageSquare, name: "Message Sync", detail: "PadSplit messages appear in your inbox" },
      { icon: BarChart3, name: "Platform Analytics", detail: "PadSplit performance metrics in your dashboard" },
    ],
    mockUI: "padsplit",
  },
  {
    id: "team",
    label: "Team & Collaboration",
    icon: Shield,
    color: "from-slate-500 to-gray-600",
    bgColor: "bg-slate-500/10",
    textColor: "text-slate-400",
    borderColor: "border-slate-500/30",
    description: "Role-based permissions, team member invites, and property-level access — collaborate securely across your entire organization.",
    features: [
      { icon: Shield, name: "Role-Based Permissions", detail: "Granular access control per feature area" },
      { icon: Users, name: "Team Member Invites", detail: "Invite team with specific access scopes" },
      { icon: Building2, name: "Property Group-Level Access", detail: "Assign team members to specific property groups" },
      { icon: MessageSquare, name: "Internal Notes & @Mentions", detail: "Tag teammates in conversations and keep internal context in one place" },
      { icon: ClipboardList, name: "Task Delegation", detail: "Assign and track tasks across team members with status visibility" },
    ],
    mockUI: "team",
  },
];

function MockUIProperty() {
  const [selected, setSelected] = useState<number | null>(null);
  const properties = [
    { addr: "1842 Oak Ave", rooms: "6 rooms", occ: "5/6", status: "Active", revenue: "$4,200/mo" },
    { addr: "3301 Elm St", rooms: "4 rooms", occ: "4/4", status: "Active", revenue: "$3,100/mo" },
    { addr: "902 Pine Rd", rooms: "8 rooms", occ: "6/8", status: "Active", revenue: "$5,800/mo" },
    { addr: "555 Maple Dr", rooms: "5 rooms", occ: "5/5", status: "Launching", revenue: "$3,750/mo" },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-blue-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">properties.dashboard</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {properties.map((p, i) => (
          <div
            key={i}
            className={`rounded-lg p-2 border cursor-pointer transition-all ${selected === i ? "bg-blue-500/15 border-blue-500/40" : "bg-white/5 border-white/5 hover:border-white/20 mock-clickable"}`}
            onClick={() => setSelected(selected === i ? null : i)}
            data-testid={`mock-property-card-${i}`}
          >
            <div className="text-xs text-white font-medium truncate">{p.addr}</div>
            <div className="text-xs text-slate-500">{p.rooms}</div>
            <div className="mt-1 flex items-center justify-between">
              <div className="text-xs text-green-400 font-semibold">{p.occ}</div>
              <div className={`w-2 h-2 rounded-full ${p.status === "Active" ? "bg-green-400" : "bg-blue-400 animate-pulse"}`} />
            </div>
          </div>
        ))}
      </div>
      {selected !== null && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-300">{properties[selected].addr}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${properties[selected].status === "Active" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"}`}>{properties[selected].status}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400">Revenue</span>
            <span className="text-green-400 font-semibold">{properties[selected].revenue}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400">Occupancy</span>
            <span className="text-white font-semibold">{properties[selected].occ}</span>
          </div>
        </div>
      )}
      {selected === null && (
        <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-lg p-2">
          <Map size={12} className="text-blue-400 flex-shrink-0" />
          <span className="text-xs text-blue-300">Click a property for details</span>
        </div>
      )}
    </div>
  );
}

function MockUIUnderwriting() {
  const [activeProperty, setActiveProperty] = useState(0);
  const deals = [
    { name: "1842 Oak Ave", sections: [["$4,200", "$1,800", "$220K", "7.8%", "12.4%", "$2,400"], "$2,400"] },
    { name: "902 Pine Rd", sections: [["$6,100", "$2,400", "$310K", "8.6%", "14.1%", "$3,700"], "$3,700"] },
  ];
  const deal = deals[activeProperty];
  const labels = ["Income", "Expenses", "Financing", "Cap Rate", "Cash-on-Cash", "NOI"];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-purple-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">underwriting.analysis</span>
      </div>
      <div className="flex gap-1 mb-2">
        {deals.map((d, i) => (
          <button
            key={i}
            onClick={() => setActiveProperty(i)}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${activeProperty === i ? "bg-purple-500/30 text-purple-300 border border-purple-500/40" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`}
            data-testid={`mock-uw-tab-${i}`}
          >
            {d.name}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        {labels.map((s, i) => (
          <div key={s} className="flex items-center justify-between bg-white/5 rounded px-2 py-1.5">
            <span className="text-xs text-slate-400">{s}</span>
            <span className="text-xs font-semibold text-white">{(deal.sections[0] as string[])[i]}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <div className="flex-1 bg-purple-500/20 border border-purple-500/30 rounded-lg p-2 text-center">
          <div className="text-sm font-bold text-purple-300">{deal.sections[1] as string}</div>
          <div className="text-xs text-slate-500">Monthly NOI</div>
        </div>
        <button className="flex-1 bg-white/5 rounded-lg p-2 flex items-center justify-center gap-1 text-xs text-slate-400" data-testid="mock-button-pdf-export">
          <FileText size={10} />
          PDF Export
        </button>
      </div>
    </div>
  );
}

function MockUIPricing() {
  const [mode, setMode] = useState<"fill" | "profit">("fill");
  const rooms = {
    fill: [
      { room: "Room 1 – Master", rate: "$925/mo", trend: "+12% occ" },
      { room: "Room 2 – Standard", rate: "$795/mo", trend: "+8% occ" },
      { room: "Room 3 – Standard", rate: "$780/mo", trend: "+6% occ" },
      { room: "Room 4 – Small", rate: "$699/mo", trend: "+15% occ" },
    ],
    profit: [
      { room: "Room 1 – Master", rate: "$1,050/mo", trend: "+$125" },
      { room: "Room 2 – Standard", rate: "$875/mo", trend: "+$80" },
      { room: "Room 3 – Standard", rate: "$850/mo", trend: "+$70" },
      { room: "Room 4 – Small", rate: "$775/mo", trend: "+$76" },
    ],
  };
  const avg = mode === "fill" ? "$799.75/mo" : "$887.50/mo";
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-green-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">pricing.rooms_overview</span>
      </div>
      <div className="flex gap-1 mb-2">
        <button onClick={() => setMode("fill")} className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${mode === "fill" ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`} data-testid="mock-pricing-fill">
          Optimize Fill Rate
        </button>
        <button onClick={() => setMode("profit")} className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${mode === "profit" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`} data-testid="mock-pricing-profit">
          Optimize Profit
        </button>
      </div>
      <div className="space-y-1.5">
        {rooms[mode].map((r, i) => (
          <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-2 py-1.5">
            <span className="text-xs text-slate-300 truncate flex-1">{r.room}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-white">{r.rate}</span>
              <span className="text-xs text-green-400">{r.trend}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 flex items-center justify-between">
        <span className="text-xs text-green-300">Portfolio Average</span>
        <span className="text-xs font-bold text-green-300">{avg}</span>
      </div>
    </div>
  );
}

function MockUIOperations() {
  const [tasks, setTasks] = useState([
    { id: 0, text: "HVAC filter – 1842 Oak", col: 0 },
    { id: 1, text: "Leaky faucet – Rm 3", col: 0 },
    { id: 2, text: "Paint touch-up – Rm 1", col: 1 },
    { id: 3, text: "Door lock replaced", col: 2 },
  ]);
  const colMeta = [
    { label: "Backlog", color: "text-slate-400", border: "border-slate-500/30" },
    { label: "In Progress", color: "text-yellow-400", border: "border-yellow-500/30" },
    { label: "Done", color: "text-green-400", border: "border-green-500/30" },
  ];
  const moveTask = (id: number) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, col: Math.min(t.col + 1, 2) } : t));
  };
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-orange-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">operations.kanban</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {colMeta.map((col, ci) => (
          <div key={col.label}>
            <div className={`text-xs font-semibold mb-2 ${col.color}`}>{col.label} ({tasks.filter(t => t.col === ci).length})</div>
            <div className="space-y-1.5 min-h-[40px]">
              {tasks.filter(t => t.col === ci).map((task) => (
                <div
                  key={task.id}
                  onClick={() => ci < 2 ? moveTask(task.id) : undefined}
                  className={`bg-white/5 border border-white/5 rounded p-1.5 text-xs text-slate-300 leading-tight transition-all ${ci < 2 ? "cursor-pointer hover:border-orange-500/30 hover:bg-orange-500/5 mock-clickable" : ""} ${ci === 2 ? "line-through opacity-60" : ""}`}
                  data-testid={`mock-task-${task.id}`}
                >
                  {task.text}
                  {ci < 2 && <ChevronRight size={8} className="inline ml-1 text-slate-500" />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-600 mt-2 text-center">Click a task to advance it</div>
    </div>
  );
}

function MockUIMembers() {
  const [threadOpen, setThreadOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setThreadOpen(true), 1000);
    return () => clearTimeout(t);
  }, []);
  const messages = [
    { name: "James R.", msg: "When is the next rent due?", time: "2m", unread: true, hasThread: false },
    { name: "Aisha M.", msg: "The shower drain is clogged again.", time: "1h", unread: true, hasThread: true },
    { name: "Carlos V.", msg: "Thanks! Got it sorted.", time: "3h", unread: false, hasThread: false },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-teal-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">members.inbox</span>
      </div>
      <div className="space-y-2">
        {messages.map((m, i) => (
          <div key={i}>
            <div
              className={`flex items-start gap-2 p-2 rounded-lg ${m.unread ? "bg-teal-500/10 border border-teal-500/20" : "bg-white/5"} ${m.hasThread ? "cursor-pointer mock-clickable" : ""}`}
              onClick={m.hasThread ? () => setThreadOpen(!threadOpen) : undefined}
              data-testid={`mock-member-msg-${i}`}
            >
              <div className="w-7 h-7 rounded-full bg-teal-500/30 flex items-center justify-center text-xs font-bold text-teal-300 flex-shrink-0">
                {m.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white">{m.name}</span>
                  <span className="text-xs text-slate-500">{m.time}</span>
                </div>
                <p className="text-xs text-slate-400 truncate">{m.msg}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                {m.unread && <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />}
                {m.hasThread && (
                  <div className="flex items-center gap-0.5 animate-pulse" title="2 internal replies">
                    <div className="w-4 h-4 rounded-full bg-brand-accent/20 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-brand-accent">2</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {m.hasThread && threadOpen && (
              <div className="ml-9 mt-1 space-y-1.5 border-l-2 border-brand-accent/30 pl-2 pb-1">
                <div className="bg-brand-accent/5 rounded p-1.5 border border-brand-accent/10">
                  <div className="flex items-center gap-1 mb-0.5">
                    <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center text-[8px] font-bold text-blue-300">S</div>
                    <span className="text-[10px] font-semibold text-blue-300">Sarah M.</span>
                    <span className="text-[10px] text-slate-500 ml-auto">45m</span>
                  </div>
                  <p className="text-[10px] text-slate-400"><span className="text-brand-accent font-semibold">@Tom</span> can you check the drain at unit 3? Might need a snake.</p>
                </div>
                <div className="bg-brand-accent/5 rounded p-1.5 border border-brand-accent/10">
                  <div className="flex items-center gap-1 mb-0.5">
                    <div className="w-4 h-4 rounded-full bg-green-500/30 flex items-center justify-center text-[8px] font-bold text-green-300">T</div>
                    <span className="text-[10px] font-semibold text-green-300">Tom K.</span>
                    <span className="text-[10px] text-slate-500 ml-auto">20m</span>
                  </div>
                  <p className="text-[10px] text-slate-400">Done — responded to the member. Scheduled plumber for tomorrow AM.</p>
                </div>
                <div className="flex items-center gap-1.5 mt-1 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-1">
                  <Clock size={9} className="text-amber-400" />
                  <span className="text-[10px] font-medium text-amber-300">Waiting reply from member</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MockUIFinancials() {
  // intentionally local — demo/mock UI widget on the public marketing landing page, not a bookmarkable page tab
  const [activeTab, setActiveTab] = useState<"pnl" | "collections">("pnl");
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-amber-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">financials.overview</span>
      </div>
      <div className="flex gap-1 mb-2">
        <button onClick={() => setActiveTab("pnl")} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${activeTab === "pnl" ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`} data-testid="mock-fin-pnl">P&L</button>
      </div>
      {activeTab === "pnl" ? (
        <div className="space-y-1.5">
          {[
            { label: "Gross Revenue", val: "$14,200", type: "income" },
            { label: "Vacancy Loss", val: "-$975", type: "loss" },
            { label: "Operating Expenses", val: "-$4,300", type: "loss" },
            { label: "Mortgage / Debt Service", val: "-$5,100", type: "loss" },
            { label: "Net Operating Income", val: "$3,825", type: "net" },
          ].map((row, i) => (
            <div key={i} className={`flex items-center justify-between px-2 py-1.5 rounded ${row.type === 'net' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-white/5'}`}>
              <span className="text-xs text-slate-400">{row.label}</span>
              <span className={`text-xs font-semibold ${row.type === 'income' ? 'text-green-400' : row.type === 'net' ? 'text-amber-300' : 'text-red-400'}`}>{row.val}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          {[
            { name: "James R.", balance: "-$125", status: "Overdue", flagged: true },
            { name: "Aisha M.", balance: "$0", status: "Current", flagged: false },
            { name: "Carlos V.", balance: "-$275", status: "Overdue", flagged: true },
            { name: "Maria L.", balance: "+$50", status: "Credit", flagged: false },
          ].map((m, i) => (
            <div key={i} className={`flex items-center justify-between px-2 py-1.5 rounded ${m.flagged ? "bg-red-500/10 border border-red-500/20" : "bg-white/5"}`}>
              <span className="text-xs text-slate-300">{m.name}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${m.flagged ? "text-red-400" : "text-green-400"}`}>{m.balance}</span>
                <span className={`text-[10px] px-1 py-0.5 rounded ${m.flagged ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}`}>{m.status}</span>
              </div>
            </div>
          ))}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded p-1.5 text-[10px] text-amber-300 text-center">Parameters: +$100 to -$299</div>
        </div>
      )}
    </div>
  );
}

function MockUIDevices() {
  const [devices, setDevices] = useState([
    { icon: "lock" as const, label: "Front Door", status: "Locked", ok: true },
    { icon: "lock" as const, label: "Room 1 Lock", status: "Unlocked", ok: false },
    { icon: "therm" as const, label: "Main Therm.", status: "72°F", ok: true },
    { icon: "therm" as const, label: "Upstairs", status: "Offline", ok: false },
  ]);
  const toggleDevice = (idx: number) => {
    setDevices(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      if (d.icon === "lock") return { ...d, status: d.status === "Locked" ? "Unlocked" : "Locked", ok: d.status !== "Locked" };
      return d;
    }));
  };
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-cyan-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">devices.overview</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {devices.map((d, i) => {
          const Icon = d.icon === "lock" ? Lock : Thermometer;
          return (
            <div
              key={i}
              className={`bg-white/5 rounded-lg p-2 flex items-center gap-2 transition-all ${d.icon === "lock" ? "cursor-pointer hover:border-cyan-500/30 hover:bg-cyan-500/5 border border-transparent mock-clickable" : ""}`}
              onClick={() => d.icon === "lock" ? toggleDevice(i) : undefined}
              data-testid={`mock-device-${i}`}
            >
              <Icon size={14} className={d.ok ? "text-cyan-400" : "text-yellow-400"} />
              <div className="min-w-0">
                <div className="text-xs text-slate-300 truncate">{d.label}</div>
                <div className={`text-xs ${d.ok ? 'text-green-400' : 'text-yellow-400'}`}>{d.status}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-600 text-center">Click locks to toggle</div>
      <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2 flex items-center justify-between">
        <span className="text-xs text-cyan-300">Active Devices</span>
        <span className="text-xs font-bold text-cyan-300">18 / 22</span>
      </div>
    </div>
  );
}

function MockUIAI() {
  const [generating, setGenerating] = useState<number | null>(null);
  const [generated, setGenerated] = useState<Set<number>>(new Set());
  const handleGenerate = (idx: number) => {
    if (generating !== null || generated.has(idx)) return;
    setGenerating(idx);
    setTimeout(() => {
      setGenerated(prev => new Set(prev).add(idx));
      setGenerating(null);
    }, 1500);
  };
  const images = [
    { label: "Bedroom", gradient: "from-blue-500/20 to-indigo-600/20" },
    { label: "Bathroom", gradient: "from-teal-500/20 to-cyan-600/20" },
    { label: "Living Room", gradient: "from-purple-500/20 to-pink-600/20" },
    { label: "Exterior", gradient: "from-green-500/20 to-emerald-600/20" },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse" />
        <span className="text-xs text-slate-400 font-mono">ai.image_generator</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {images.map((img, i) => (
          <div
            key={i}
            onClick={() => handleGenerate(i)}
            className={`h-16 rounded-lg bg-gradient-to-br ${img.gradient} border border-white/10 flex items-end p-1.5 cursor-pointer transition-all hover:border-pink-500/30 relative overflow-hidden ${!generated.has(i) && generating !== i ? "mock-clickable" : ""}`}
            data-testid={`mock-ai-img-${i}`}
          >
            {generating === i && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <div className="w-4 h-4 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {generated.has(i) && (
              <div className="absolute top-1 right-1">
                <CheckCircle size={10} className="text-green-400" />
              </div>
            )}
            <span className="text-xs text-white/60">{img.label}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-600 text-center">Click a room to generate AI staging</div>
      <div className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/20 rounded-lg p-2">
        <Bot size={12} className="text-pink-400 flex-shrink-0" />
        <span className="text-xs text-pink-300">Powered by AI</span>
      </div>
    </div>
  );
}

function MockUIPadSplit() {
  const [syncing, setSyncing] = useState(false);
  const [counts, setCounts] = useState({ members: 24, rooms: 32, messages: 187 });
  const runSync = () => {
    if (syncing) return;
    setSyncing(true);
    setTimeout(() => {
      setCounts(prev => ({ members: prev.members + Math.floor(Math.random() * 3), rooms: prev.rooms + Math.floor(Math.random() * 2), messages: prev.messages + Math.floor(Math.random() * 12) + 1 }));
      setSyncing(false);
    }, 2000);
  };
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-2 h-2 bg-indigo-400 rounded-full ${syncing ? "animate-pulse" : ""}`} />
        <span className="text-xs text-slate-400 font-mono">padsplit.sync_engine</span>
      </div>
      <div className="space-y-2">
        {[
          { label: "Members synced", val: String(counts.members) },
          { label: "Rooms synced", val: String(counts.rooms) },
          { label: "Messages imported", val: String(counts.messages) },
          { label: "Last sync", val: syncing ? "Syncing..." : "Just now" },
        ].map((row, i) => (
          <div key={i} className="flex items-center justify-between bg-white/5 rounded px-2 py-1.5">
            <span className="text-xs text-slate-400">{row.label}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold ${syncing && i < 3 ? "text-indigo-300" : "text-white"}`}>{row.val}</span>
              {!syncing && <CheckCircle size={10} className="text-green-400" />}
              {syncing && i < 3 && <div className="w-2.5 h-2.5 border border-indigo-400 border-t-transparent rounded-full animate-spin" />}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={runSync}
        disabled={syncing}
        className={`w-full py-1.5 rounded text-xs font-medium transition-colors ${syncing ? "bg-indigo-500/20 text-indigo-400 cursor-not-allowed" : "bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 border border-indigo-500/30 mock-clickable"}`}
        data-testid="mock-sync-btn"
      >
        {syncing ? "Syncing..." : "Run Sync Now"}
      </button>
    </div>
  );
}

function MockUITeam() {
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const members = [
    { name: "Sarah M.", role: "Admin", access: "All Properties", tasks: 3, notes: 5, lastActive: "2m ago" },
    { name: "Tom K.", role: "Manager", access: "Oak Ave, Elm St", tasks: 7, notes: 2, lastActive: "15m ago" },
    { name: "Jessica L.", role: "Member", access: "Pine Rd only", tasks: 1, notes: 0, lastActive: "1h ago" },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-slate-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">team.members</span>
      </div>
      <div className="space-y-2">
        {members.map((m, i) => (
          <div key={i}>
            <div
              className={`flex items-center gap-2 rounded-lg p-2 cursor-pointer transition-all ${expandedMember === i ? "bg-blue-500/10 border border-blue-500/20" : "bg-white/5 hover:bg-white/10 mock-clickable"}`}
              onClick={() => setExpandedMember(expandedMember === i ? null : i)}
              data-testid={`mock-team-member-${i}`}
            >
              <div className="w-7 h-7 rounded-full bg-slate-500/30 flex items-center justify-center text-xs font-bold text-slate-300 flex-shrink-0">
                {m.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white">{m.name}</div>
                <div className="text-xs text-slate-500">{m.access}</div>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${m.role === 'Admin' ? 'bg-blue-500/20 text-blue-400' : m.role === 'Manager' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>{m.role}</span>
            </div>
            {expandedMember === i && (
              <div className="ml-9 mt-1 space-y-1 border-l-2 border-blue-500/30 pl-2 pb-1">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Assigned Tasks</span>
                  <span className="text-white font-semibold">{m.tasks}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Internal Notes</span>
                  <span className="text-white font-semibold">{m.notes}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Last Active</span>
                  <span className="text-green-400 font-semibold">{m.lastActive}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 bg-slate-500/10 border border-slate-500/20 rounded-lg p-2 mt-2">
        <MessageSquare size={10} className="text-slate-400 flex-shrink-0" />
        <span className="text-xs text-slate-300">2 internal @mentions pending</span>
      </div>
    </div>
  );
}

function MockUIDirectBooking() {
  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);
  const rooms = [
    { room: "Room 2 – Standard", price: "$825/mo", status: "Available" },
    { room: "Room 5 – Large", price: "$950/mo", status: "Available" },
    { room: "Room 1 – Master", price: "$975/mo", status: "Occupied" },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-brand-accent rounded-full animate-pulse" />
        <span className="text-xs text-slate-400 font-mono">booking.branded_site</span>
      </div>
      <div className="bg-brand-accent/5 rounded-lg p-3 border border-brand-accent/20">
        <div className="text-xs font-semibold text-brand-accent mb-1">yourcompany.padpulse.app</div>
        <div className="text-xs text-slate-400">Verified & branded booking site</div>
      </div>
      <div className="space-y-2">
        {rooms.map((r, i) => (
          <div key={i}>
            <div
              className={`flex items-center justify-between rounded-lg px-2 py-2 transition-all ${r.status === "Available" ? "cursor-pointer hover:bg-brand-accent/5" : ""} ${selectedRoom === i ? "bg-brand-accent/10 border border-brand-accent/20" : `bg-white/5 ${r.status === "Available" ? "mock-clickable" : ""}`}`}
              onClick={() => r.status === "Available" ? setSelectedRoom(selectedRoom === i ? null : i) : undefined}
              data-testid={`mock-booking-room-${i}`}
            >
              <div className="min-w-0">
                <div className="text-xs text-slate-300 truncate">{r.room}</div>
                <div className="text-xs text-slate-500">{r.price}</div>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${r.status === "Available" ? "bg-green-500/20 text-green-400" : "bg-white/10 text-slate-400"}`}>{r.status}</span>
            </div>
            {selectedRoom === i && r.status === "Available" && (
              <div className="mt-1 ml-2 bg-brand-accent/5 border border-brand-accent/10 rounded p-2 space-y-1">
                <div className="text-[10px] text-brand-accent font-semibold">Quick Inquiry Preview</div>
                <div className="flex gap-1">
                  <div className="flex-1 bg-white/5 rounded px-1.5 py-1 text-[10px] text-slate-500">Name...</div>
                  <div className="flex-1 bg-white/5 rounded px-1.5 py-1 text-[10px] text-slate-500">Phone...</div>
                </div>
                <div className="bg-brand-accent/20 rounded px-2 py-1 text-[10px] text-brand-accent font-medium text-center">Submit Inquiry</div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-lg p-2">
        <div className="flex items-center gap-1.5 mb-1">
          <Shield size={10} className="text-brand-accent" />
          <span className="text-xs font-semibold text-brand-accent">Referral Source Protected</span>
        </div>
        <span className="text-xs text-slate-400">Saving $250–$300 per booking (no 10-day fees)</span>
      </div>
    </div>
  );
}

function MockUIListingManagement() {
  const [expandedPlatform, setExpandedPlatform] = useState<number | null>(null);
  const channels = [
    { platform: "Zillow", status: "Active", ok: true, listings: 12, views: "2.4k" },
    { platform: "Facebook Marketplace", status: "Active", ok: true, listings: 8, views: "1.8k" },
    { platform: "Roomies", status: "Active", ok: true, listings: 6, views: "920" },
    { platform: "HotPads", status: "2 stale", ok: false, listings: 4, views: "310" },
    { platform: "SpareRoom", status: "Active", ok: true, listings: 5, views: "680" },
    { platform: "Furnished Finders", status: "Active", ok: true, listings: 3, views: "450" },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 bg-fuchsia-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">listings.channels</span>
      </div>
      <div className="space-y-1.5">
        {channels.map((ch, i) => (
          <div key={i}>
            <div
              className={`flex items-center justify-between rounded px-2 py-1.5 cursor-pointer transition-all ${expandedPlatform === i ? "bg-fuchsia-500/10 border border-fuchsia-500/20" : "bg-white/5 hover:bg-white/10 mock-clickable"}`}
              onClick={() => setExpandedPlatform(expandedPlatform === i ? null : i)}
              data-testid={`mock-listing-platform-${i}`}
            >
              <span className="text-xs text-slate-300">{ch.platform}</span>
              <div className="flex items-center gap-1.5">
                <span className={`text-xs ${ch.ok ? "text-green-400" : "text-yellow-400"}`}>{ch.status}</span>
                <div className={`w-1.5 h-1.5 rounded-full ${ch.ok ? "bg-green-400" : "bg-yellow-400"}`} />
              </div>
            </div>
            {expandedPlatform === i && (
              <div className="ml-2 mt-0.5 bg-fuchsia-500/5 border-l-2 border-fuchsia-500/30 pl-2 py-1 space-y-0.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Active Listings</span>
                  <span className="text-white font-semibold">{ch.listings}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Monthly Views</span>
                  <span className="text-fuchsia-300 font-semibold">{ch.views}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-lg p-2 flex items-center justify-between">
        <span className="text-xs text-fuchsia-300">Total Reach</span>
        <span className="text-xs font-bold text-fuchsia-300">6+ platforms</span>
      </div>
    </div>
  );
}

function MockUIDashboard() {
  const [view, setView] = useState<"portfolio" | "property">("portfolio");
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-rose-400 rounded-full" />
        <span className="text-xs text-slate-400 font-mono">dashboard.overview</span>
      </div>
      <div className="flex gap-1 mb-2">
        <button onClick={() => setView("portfolio")} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${view === "portfolio" ? "bg-rose-500/20 text-rose-300 border border-rose-500/30" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`} data-testid="mock-dash-portfolio">Portfolio</button>
        <button onClick={() => setView("property")} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${view === "property" ? "bg-rose-500/20 text-rose-300 border border-rose-500/30" : "bg-white/5 text-slate-500 hover:bg-white/10 mock-clickable"}`} data-testid="mock-dash-property">1842 Oak Ave</button>
      </div>
      {view === "portfolio" ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Occupancy", val: "87%", color: "text-green-400", bg: "bg-green-500/10" },
              { label: "Monthly NOI", val: "$9,240", color: "text-amber-300", bg: "bg-amber-500/10" },
              { label: "Open Tasks", val: "4", color: "text-orange-400", bg: "bg-orange-500/10" },
              { label: "Devices Online", val: "18/22", color: "text-cyan-400", bg: "bg-cyan-500/10" },
            ].map((stat, i) => (
              <div key={i} className={`${stat.bg} rounded-lg p-2 text-center border border-white/5`}>
                <div className={`text-base font-bold ${stat.color}`}>{stat.val}</div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded px-2 py-1.5">
              <Wrench size={10} className="text-orange-400 flex-shrink-0" />
              <span className="text-xs text-orange-300">HVAC filter overdue – 1842 Oak Ave</span>
            </div>
            <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded px-2 py-1.5">
              <Thermometer size={10} className="text-yellow-400 flex-shrink-0" />
              <span className="text-xs text-yellow-300">Thermostat offline – 3301 Elm St</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Occupancy", val: "5/6", color: "text-green-400", bg: "bg-green-500/10" },
              { label: "Revenue", val: "$4,200", color: "text-amber-300", bg: "bg-amber-500/10" },
              { label: "Open Tasks", val: "2", color: "text-orange-400", bg: "bg-orange-500/10" },
              { label: "Devices", val: "6/6", color: "text-cyan-400", bg: "bg-cyan-500/10" },
            ].map((stat, i) => (
              <div key={i} className={`${stat.bg} rounded-lg p-2 text-center border border-white/5`}>
                <div className={`text-base font-bold ${stat.color}`}>{stat.val}</div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded px-2 py-1.5">
              <Wrench size={10} className="text-orange-400 flex-shrink-0" />
              <span className="text-xs text-orange-300">HVAC filter – overdue by 3 days</span>
            </div>
            <div className="flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded px-2 py-1.5">
              <MessageSquare size={10} className="text-teal-400 flex-shrink-0" />
              <span className="text-xs text-teal-300">1 unread message from Aisha M.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const mockUIMap: Record<string, () => JSX.Element> = {
  property: MockUIProperty,
  underwriting: MockUIUnderwriting,
  pricing: MockUIPricing,
  operations: MockUIOperations,
  members: MockUIMembers,
  financials: MockUIFinancials,
  devices: MockUIDevices,
  ai: MockUIAI,
  padsplit: MockUIPadSplit,
  team: MockUITeam,
  directBooking: MockUIDirectBooking,
  listingManagement: MockUIListingManagement,
  dashboard: MockUIDashboard,
};


const AUTO_CYCLE_INTERVAL = 5000;

interface FeatureExplorerProps {
  onCtaClick: () => void;
  ctaLabel?: string;
  previewMode?: "illustrations" | "screenshots";
}

export function FeatureExplorer({ onCtaClick, ctaLabel = "Start free", previewMode = "illustrations" }: FeatureExplorerProps) {
  const [activeFeatureGroup, setActiveFeatureGroup] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusPaused, setIsFocusPaused] = useState(false);
  const [isMediaExpanded, setIsMediaExpanded] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const isPaused = isHovered || isFocusPaused || isMediaExpanded || manuallyPaused;
  const [reducedMotion, setReducedMotion] = useState(false);
  const [progress, setProgress] = useState(0);
  const tabStripRef = useRef<HTMLDivElement>(null);
  const tabStripInnerRef = useRef<HTMLDivElement>(null);
  const tabStripTailRef = useRef<HTMLDivElement>(null);
  const progressTimerRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);
  const featureExplorerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  const advanceTab = useCallback(() => {
    setActiveFeatureGroup((prev) => (prev + 1) % featureGroups.length);
    setProgress(0);
    lastTickRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const el = featureExplorerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion || isPaused || !isInView) {
      lastTickRef.current = 0;
      return;
    }
    lastTickRef.current = Date.now();
    const frame = () => {
      if (!lastTickRef.current) return;
      const elapsed = Date.now() - lastTickRef.current;
      const pct = Math.min((elapsed / AUTO_CYCLE_INTERVAL) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        advanceTab();
        return;
      }
      progressTimerRef.current = requestAnimationFrame(frame);
    };
    progressTimerRef.current = requestAnimationFrame(frame);
    return () => {
      if (progressTimerRef.current) cancelAnimationFrame(progressTimerRef.current);
    };
  }, [reducedMotion, isPaused, isInView, activeFeatureGroup, advanceTab]);

  const alignActiveTab = useCallback((behavior: ScrollBehavior) => {
    const strip = tabStripRef.current;
    if (!strip) return;
    const activeBtn = strip.querySelector<HTMLElement>('button[data-tab-index][aria-pressed="true"]');
    if (!activeBtn) return;
    // Viewport rects share a coordinate system; offsetLeft can include a distant
    // positioned ancestor and overshoot when the toolkit is centered on the page.
    const left = Math.max(0, strip.scrollLeft + activeBtn.getBoundingClientRect().left - strip.getBoundingClientRect().left - 8);
    strip.scrollTo({ left, behavior });
  }, []);

  useEffect(() => {
    const strip = tabStripRef.current;
    const tail = tabStripTailRef.current;
    if (!strip || !tail) return;
    const resize = () => {
      const last = strip.querySelector<HTMLElement>(`[data-tab-index="${featureGroups.length - 1}"]`);
      // Enough trailing room lets even the last tab occupy the leftmost slot.
      tail.style.width = `${Math.max(0, strip.clientWidth - (last?.getBoundingClientRect().width ?? 0) - 16)}px`;
      alignActiveTab("auto");
    };
    resize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }
    const observer = new ResizeObserver(resize);
    observer.observe(strip);
    const last = strip.querySelector<HTMLElement>(`[data-tab-index="${featureGroups.length - 1}"]`);
    if (last) observer.observe(last);
    return () => observer.disconnect();
  }, [alignActiveTab]);

  useEffect(() => {
    alignActiveTab(reducedMotion ? "auto" : "smooth");
  }, [activeFeatureGroup, reducedMotion, alignActiveTab]);

  const activeGroup = featureGroups[activeFeatureGroup];
  const MockUI = mockUIMap[activeGroup.mockUI];
  const ActiveIcon = activeGroup.icon;

  return (
    <div
      ref={featureExplorerRef}
      className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 shadow-2xl"
      data-testid="feature-explorer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setProgress(0); lastTickRef.current = Date.now(); }}
      onPointerDownCapture={() => setIsFocusPaused(false)}
      onKeyDownCapture={() => setIsFocusPaused(true)}
      onFocusCapture={(event) => setIsFocusPaused(event.target instanceof HTMLElement && event.target.matches(":focus-visible"))}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsFocusPaused(false);
      }}
    >
      <div className="h-1 bg-gray-800 relative overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-accent to-[#ffcc00] transition-none"
          style={{ width: `${progress}%` }}
          data-testid="progress-bar"
        />
      </div>
      <div className="flex justify-end bg-gray-950/50 px-3 py-1.5">
        {reducedMotion ? (
          <span className="text-xs text-gray-400">Automatic tour paused for reduced motion</span>
        ) : (
          <button
            type="button"
            className="rounded px-2 py-1 text-xs text-gray-300 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent"
            aria-label={manuallyPaused ? "Resume automatic tour" : "Pause automatic tour"}
            aria-pressed={manuallyPaused}
            onClick={() => setManuallyPaused((paused) => !paused)}
            data-testid="button-toolkit-autoplay"
          >
            {manuallyPaused ? "Resume tour" : "Pause tour"}
          </button>
        )}
      </div>
      <div ref={tabStripRef} data-testid="toolkit-tab-strip" className="overflow-x-auto scrollbar-none bg-gray-950/50">
        <div ref={tabStripInnerRef} className="flex gap-0.5 px-2 pt-2 min-w-max items-end">
          {featureGroups.map((group, index) => {
            const Icon = group.icon;
            const isActive = activeFeatureGroup === index;
            const isBooking = group.id === "direct-booking";
            return (
              <button
                key={group.id}
                data-tab-index={index}
                onClick={() => {
                  if (activeFeatureGroup === index) alignActiveTab(reducedMotion ? "auto" : "smooth");
                  setActiveFeatureGroup(index);
                  setProgress(0);
                  lastTickRef.current = Date.now();
                }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-t-lg text-xs font-medium whitespace-nowrap transition-all duration-200 focus:outline-none border-x border-t ${
                  isActive
                    ? isBooking
                      ? "bg-brand-accent/15 text-brand-accent border-brand-accent/30 pb-2.5 -mb-px z-10 shadow-sm"
                      : "bg-gray-900 text-white border-gray-700 pb-2.5 -mb-px z-10 shadow-sm"
                    : isBooking
                      ? "text-brand-accent/60 hover:text-brand-accent bg-gray-800/50 border-gray-800 hover:bg-brand-accent/10"
                      : "text-gray-500 hover:text-gray-300 bg-gray-800/50 border-gray-800 hover:bg-gray-800"
                } focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-accent`}
                aria-pressed={isActive}
                data-testid={`tab-feature-${group.id}`}
              >
                <Icon size={13} />
                {group.label}
              </button>
            );
          })}
          <div ref={tabStripTailRef} aria-hidden="true" className="shrink-0" />
        </div>
        <div className="h-px bg-gray-700" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-0">
        <div className="lg:col-span-3 p-6 md:p-8">
          <div className={`inline-flex items-center gap-2 ${activeGroup.bgColor} rounded-full px-3 py-1 text-xs font-semibold ${activeGroup.textColor} mb-4`}>
            <ActiveIcon size={12} />
            {activeGroup.label}
          </div>
          <p className="text-gray-300 text-sm leading-relaxed mb-2" data-testid="text-feature-description">
            {activeGroup.description}
          </p>
          {activeGroup.id === "smart-devices" ? (
            <p className="text-gray-500 text-xs mb-6" data-testid="text-smart-lock-footnote">
              *Some lock types come with additional charges.
            </p>
          ) : (
            <div className="mb-6" />
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {activeGroup.features.map((feature, i) => {
              const FIcon = feature.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-2.5 group"
                  data-testid={`feature-item-${activeGroup.id}-${i}`}
                >
                  <div className={`flex-shrink-0 w-7 h-7 rounded-lg ${activeGroup.bgColor} flex items-center justify-center mt-0.5`}>
                    <FIcon size={13} className={activeGroup.textColor} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{feature.name}</div>
                    <div className="text-xs text-gray-400 leading-relaxed">{feature.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {previewMode === "screenshots" && ["members", "ai", "padsplit"].includes(activeGroup.id) && (
            <p className="mt-5 text-xs leading-5 text-slate-300">
              Continuous Auto Draft requires beta access and enabling; signup or payment alone does not grant access.
            </p>
          )}
          <div className="mt-6">
            <button
              onClick={onCtaClick}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${activeGroup.textColor} hover:opacity-80 transition-opacity focus:outline-none focus:underline`}
              data-testid="button-feature-cta"
            >
              {ctaLabel} <ChevronRight size={13} />
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-[#0a1628] border-t md:border-t-0 md:border-l border-gray-800">
          {previewMode === "screenshots" ? (
            <FeatureExplorerMedia key={activeGroup.id} groupId={activeGroup.id} illustration={<MockUI />} onExpandedChange={setIsMediaExpanded} />
          ) : (
            <>
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-gray-800">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
            <span className="ml-2 text-xs text-slate-500 font-mono">padpulse.app</span>
          </div>
          <div data-testid={`mock-ui-${activeGroup.id}`}>
            <MockUI />
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
